// src/components/ChatWidget.jsx
import React, { useEffect, useState, useMemo } from "react";
import {
  Chat as StreamChatComponent,
  Channel,
  Window,
  ChannelHeader,
  MessageList,
  MessageInput,
  Thread,
} from "stream-chat-react";
import { StreamChat } from "stream-chat";
import "stream-chat-react/dist/css/v2/index.css";
import { IconButton, Box } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from "react-redux";

const STREAM_API_KEY = "r2np5pp6p7w7";

export default function ChatModal({ open, onClose, clientUser }) {
  // Redux
  const token = useSelector((s) => s.attorneyData.token);
  const lawyer = useSelector((s) => s.attorneyData.attorneyData);

  const [client, setClient] = useState(null);

  // 1) Initialize Stream client when open
  useEffect(() => {
    if (!open || !token || !lawyer?.user_id || !lawyer.name) {
      return;
    }
    const chatClient = StreamChat.getInstance(STREAM_API_KEY);
    chatClient
      .connectUser({ id: lawyer.user_id, name: lawyer.name }, token)
      .then(() => {
        setClient(chatClient);
      })
      .catch((err) => {
        console.error("❌ Error connecting Stream client:", err);
      });
    return () => {
      chatClient.disconnectUser();
      setClient(null);
    };
  }, [open, token, lawyer?.user_id, lawyer?.name]);

  // 2) Build one-to-one channel
  const channel = useMemo(() => {
    if (!client || !clientUser?.user_id) {
      return null;
    }
    const ch = client.channel("messaging", {
      members: [lawyer.user_id, clientUser.user_id],
    });
    return ch;
  }, [client, lawyer?.user_id, clientUser]);

  // 3) Watch channel
  useEffect(() => {
    if (!channel) {
      return;
    }
    let active = true;
    channel
      .watch()
      .then(() => {
        if (!active) {
          channel.stopWatching();
        }
      })
      .catch((err) => {
        console.error("❌ Error watching channel:", err);
      });
    return () => {
      active = false;
      channel.stopWatching();
    };
  }, [channel]);

  // 4) Render nothing when closed
  if (!open) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 24,
        right: 24,
        width: "25%",
        height: "60%",
        boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
        borderRadius: "10px",
        zIndex: 1300,
        backgroundColor: "#fff",
        display: "flex",
        flexDirection: "column",
        paddingTop: "10px",
        paddingBottom: "20px",
      }}
    >
      {/* Header with close button */}
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: "1px solid #e0e0e0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ fontWeight: 500 }}>Chat with {clientUser?.name}</Box>

        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Chat body */}
      {client && channel ? (
        <StreamChatComponent client={client} theme="messaging light">
          <Channel channel={channel}>
            <Window
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                flex: 1,
              }}
            >
              <ChannelHeader />
              <MessageList style={{ flex: 1, overflowY: "auto", padding: 8 }} />
              <MessageInput style={{ padding: 8 }} />
            </Window>
            <Thread />
          </Channel>
        </StreamChatComponent>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          Loading chat…
        </Box>
      )}
    </Box>
  );
}
